import {
  useBeforeUpload,
  useOnUploadMedia,
  useRemoveFileFromStorage
} from 'hooks'
import { useCallback, useEffect, useState } from 'react'

import { UPLOADING_STATUSES } from '__constants__'
import { message } from 'antd'
import { useTranslations } from 'contexts/Translation'

const useUploadMedia = (props) => {
  const {
    value,
    onChange,
    storagePath,
    setUploadLoading,
    tipId,
    setStep,
    isGuestMode,
    maxCount
  } = props
  const [files, setFiles] = useState(value || null)
  const { t } = useTranslations()

  const [loadings, setLoadings] = useState([])

  const defer = useCallback((fn) => {
    if (typeof fn !== 'function') return
    if (typeof queueMicrotask === 'function') return queueMicrotask(fn)
    return Promise.resolve().then(fn)
  }, [])

  useEffect(() => {
    const isUploading = loadings?.find((item) => item === false)
    const mediaWithoutUrl = value?.files?.find((item) => !item?.url)
    !mediaWithoutUrl &&
      typeof setUploadLoading === 'function' &&
      setUploadLoading(null)

    loadings?.length &&
      !isUploading &&
      !mediaWithoutUrl &&
      typeof setStep === 'function' &&
      setStep((step) => step + 1)
  }, [loadings, setStep, setUploadLoading, value])

  /* A callback function that is used to upload the file to the storage. */
  const { onUpload } = useOnUploadMedia({
    setLoadings,
    setFiles,
    onChange,
    storagePath,
    tipId,
    isGuestMode
  })

  const beforeUpload = useBeforeUpload()
  const removeFileFromStorage = useRemoveFileFromStorage()

  /**
   * `handleChange` is a function that takes an object as an argument, and that object has a property
   * called `fileList` which is assigned to a variable called `newFileList`
   */

  const handleChange = useCallback(
    (e) => {
      const { fileList: newFileList, file } = e

      if (!file?.response && file?.status !== UPLOADING_STATUSES.UPLOADING)
        return

      const computedStatus = file?.file?.status || file?.status
      if (computedStatus === UPLOADING_STATUSES.REMOVED) return

      setFiles((prev) => {
        const next = { ...prev, files: newFileList }
        defer(() => onChange?.(next))
        return next
      })
    },
    [defer, onChange]
  )

  const customRequest = (uploadValue) => {
    setFiles((prev) => {
      const newData = {
        ...prev,
        uploadFiles: [...(prev?.uploadFiles || []), uploadValue]
      }
      defer(() => onChange?.(newData))
      return newData
    })

    setTimeout(() => {
      uploadValue?.onSuccess('ok')
    }, 0)
  }

  const handleUpload = useCallback(() => {
    setUploadLoading?.(true)
    const filterUploadData = files?.uploadFiles

    const images = value?.files?.filter(({ type }) =>
      type?.includes('image')
    )?.length

    const video = value?.files?.filter(({ type }) =>
      type?.includes('video')
    )?.length

    if (maxCount === 1 && (images || 0) + (video || 0) > 1) {
      setUploadLoading?.(false)
      return message.error('Only 1 file can be uploaded.')
    }

    if (images > 3 || video > 1) {
      setUploadLoading?.(false)
      return message.error(
        `${t(
          'It is impossible to upload the file, the maximum number of files: 3 photos and 1 video'
        )}.`
      )
    }

    filterUploadData?.length &&
      setLoadings(Array(filterUploadData.length).fill(false))

    filterUploadData?.map((uploadData) => onUpload(uploadData))
  }, [
    files?.uploadFiles,
    maxCount,
    onUpload,
    setUploadLoading,
    t,
    value?.files
  ])

  // It removes the file from the local state
  const handleRemoveLocalState = (file) => {
    setFiles((prev) => {
      let newData = { ...prev }

      const computedFiles = newData?.files?.filter(
        (item) => item?.uid !== file?.uid
      )
      const computedUploadFiles = newData?.uploadFiles?.filter(
        (item) => item?.file?.uid !== file?.uid
      )

      const computedData = {
        uploadFiles: [...computedUploadFiles],
        files: [...computedFiles]
      }

      defer(() => onChange?.(computedData))

      return computedData
    })
  }

  const handleRemoveFile = (file) => {
    if (file?.url) {
      removeFileFromStorage(file?.url)
    }

    handleRemoveLocalState(file)
  }

  return {
    files,
    onUpload,
    customRequest,
    beforeUpload,
    handleUpload,
    handleChange,
    handleRemoveFile
  }
}

export default useUploadMedia
